<template>
  <div class="permission-sod-addon">
    <div class="addon-head">
      <div>
        <span>HRM_PERMISSION_SOD_RISK</span>
        <h2>权限职责分离风险</h2>
        <p>聚合角色、菜单和人员授权，识别高权角色、职责冲突、无权限角色、多角色人员和禁用授权残留。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">角色权限强度排行</div>
          <el-table :data="overview.rolePrivilegeRanking" size="small" v-loading="loading">
            <el-table-column prop="role_name" label="角色" min-width="120" />
            <el-table-column prop="role_code" label="编码" min-width="120" />
            <el-table-column prop="menu_count" label="菜单数" width="80" />
            <el-table-column prop="permission_admin_count" label="权限管理" width="90" />
            <el-table-column prop="finance_sensitive_count" label="财务敏感" width="90" />
            <el-table-column prop="org_sensitive_count" label="组织敏感" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">敏感菜单覆盖</div>
          <div class="status-list">
            <div v-for="item in overview.sensitiveMenuCoverage" :key="item.menu_code" class="status-item">
              <span>{{ item.menu_name }}</span>
              <strong>{{ item.staff_count }}</strong>
              <em>{{ item.role_count }} 个角色</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">职责冲突人员</div>
          <el-table :data="overview.sodConflictStaffList" size="small" v-loading="loading" empty-text="暂无冲突">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="role_names" label="角色" min-width="180" />
            <el-table-column prop="permission_admin_count" label="权限管理" width="90" />
            <el-table-column prop="finance_sensitive_count" label="财务敏感" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">多角色人员</div>
          <el-table :data="overview.multiRoleStaffList" size="small" v-loading="loading" empty-text="暂无多角色">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="role_count" label="角色数" width="80" />
            <el-table-column prop="role_names" label="角色" min-width="150" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">空权限角色</div>
          <el-table :data="overview.emptyRoleList" size="small" v-loading="loading" empty-text="暂无空角色">
            <el-table-column prop="role_name" label="角色" min-width="120" />
            <el-table-column prop="role_code" label="编码" min-width="120" />
            <el-table-column prop="assigned_staff_count" label="分配人数" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">禁用授权残留</div>
          <div class="status-list">
            <div v-for="item in overview.disabledGrantResidue" :key="item.grant_type" class="status-item">
              <span>{{ item.grant_type }}</span>
              <strong>{{ item.residue_count }}</strong>
              <em>条残留</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getPermissionSodRiskOverview } from '../api/permissionSodRisk'

export default {
  name: 'PermissionSodRiskOverview',
  data () {
    return {
      loading: false,
      overview: {
        roleCount: 0,
        staffWithRoleCount: 0,
        noRoleActiveStaffCount: 0,
        multiRoleStaffCount: 0,
        emptyRoleCount: 0,
        highPrivilegeRoleCount: 0,
        rolePrivilegeRanking: [],
        sodConflictStaffList: [],
        multiRoleStaffList: [],
        emptyRoleList: [],
        disabledGrantResidue: [],
        sensitiveMenuCoverage: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '角色总数', value: this.overview.roleCount, meta: `${this.overview.staffWithRoleCount || 0} 人已授权` },
        { label: '无角色在职', value: this.overview.noRoleActiveStaffCount, meta: '登录后无业务权限', tone: 'orange' },
        { label: '多角色人员', value: this.overview.multiRoleStaffCount, meta: '需复核职责边界', tone: 'blue' },
        { label: '高权角色', value: this.overview.highPrivilegeRoleCount, meta: `${this.overview.emptyRoleCount || 0} 个空角色`, tone: 'red' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getPermissionSodRiskOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '权限职责分离风险加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="less" scoped>
.permission-sod-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #991b1b;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.status-list {
  display: grid;
  gap: 10px;
}

.status-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.status-item span,
.status-item em {
  color: #5f6f89;
  font-style: normal;
}

.status-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
